import { Component, Input, OnInit } from "@angular/core";
import { ReplaySubject, Subject } from "rxjs";
import * as _ from "lodash";
import { filter, flatMap, tap } from "rxjs/operators";
import { CompanyUser } from "models/companyusers/CompanyUser";
import { EmploymentPeriod } from "models/users/EmploymentPeriod";
import { UserEmploymentPeriodService } from "services/user/UserEmploymentPeriodService";
import { Action } from "../../genericmodal/GenericModalComponent";
import { I18NService } from "services/i18n/I18NService";
import { userEmploymentI18N } from "./i18n/user-employment-i18n";
import { DateService } from "services/date/DateService";

type ModalView = "list" | "form";

@Component({
    selector: "user-employment-modal",
    templateUrl: "user-employment-modal.html",
    host: { "[class.c-user-employment-modal]": "true" },
})
export class UserEmploymentModalComponent implements OnInit {
    @Input() public user: CompanyUser;
    @Input() public showStream: Subject<boolean>;

    public actionStream = new Subject<Action>();
    public periods: EmploymentPeriod[] = [];
    public ready = false;
    public view: ModalView = "list";
    public selectedPeriod: EmploymentPeriod | null = null;
    public fromList = false;
    public stillEmployed = true;

    public startDate: Date | null = null;
    public endDate: Date | null = null;

    public startDateSubject = new ReplaySubject<Date>(1);
    public endDateSubject = new ReplaySubject<Date>(1);
    public preselectedStartStream = new ReplaySubject<Date>(1);
    public preselectedEndStream = new ReplaySubject<Date>(1);

    public i18n;

    public get canSave(): boolean {
        return this.view === "form" && this.startDate !== null;
    }

    public get showReset(): boolean {
        return this.view === "form" && this.selectedPeriod !== null;
    }

    constructor(
        private employmentPeriodService: UserEmploymentPeriodService,
        i18nService: I18NService
    ) {
        this.i18n = i18nService.extractCurrentTranslation(userEmploymentI18N);
    }

    public ngOnInit(): void {
        this.startDateSubject.subscribe((d) => {
            this.startDate = d;
        });
        this.endDateSubject.subscribe((d) => {
            this.endDate = d;
        });

        this.showStream
            .pipe(
                filter(_.identity),
                tap(() => {
                    this.ready = false;
                }),
                flatMap(() =>
                    this.employmentPeriodService.getEmploymentPeriods(
                        this.user.id
                    )
                )
            )
            .subscribe((periods) => {
                this.periods = periods;
                if (periods.length === 0) {
                    this.openCreateForm(false);
                } else if (periods.length === 1) {
                    this.openEditForm(periods[0], false);
                } else {
                    this.view = "list";
                }
                this.ready = true;
            });

        this.actionStream.subscribe((action) => {
            if (action === Action.OK) {
                this.save();
            }
            if (action === Action.RESET) {
                this.deletePeriod();
            }
        });
    }

    public selectPeriod(period: EmploymentPeriod): void {
        this.openEditForm(period, true);
    }

    public startCreate(): void {
        this.openCreateForm(this.periods.length > 1);
    }

    public goBack(): void {
        this.view = "list";
    }

    public onStillEmployedChange(): void {
        if (this.stillEmployed) {
            this.endDate = null;
        }
    }

    public formatDate(date: Date): string {
        return date ? DateService.toISOString(date) : "";
    }

    private openEditForm(period: EmploymentPeriod, fromList: boolean): void {
        this.selectedPeriod = period;
        this.fromList = fromList;
        this.startDate = period.startDate;
        this.endDate = period.endDate || null;
        this.stillEmployed = !period.endDate;
        this.preselectedStartStream.next(period.startDate);
        this.preselectedEndStream.next(period.endDate || new Date());
        this.view = "form";
    }

    private openCreateForm(fromList: boolean): void {
        this.selectedPeriod = null;
        this.fromList = fromList;
        this.endDate = null;
        this.stillEmployed = true;
        const today = new Date();
        this.startDate = today;
        this.preselectedStartStream.next(today);
        this.startDateSubject.next(today);
        this.preselectedEndStream.next(today);
        this.view = "form";
    }

    private save(): void {
        const endDate = this.stillEmployed ? null : this.endDate;
        let observable;
        if (this.selectedPeriod !== null) {
            observable = this.employmentPeriodService.updateEmploymentPeriod(
                this.user.id,
                this.selectedPeriod.id,
                this.startDate,
                endDate
            );
        } else {
            observable = this.employmentPeriodService.addEmploymentPeriod(
                this.user.id,
                this.startDate,
                endDate
            );
        }
        observable.subscribe();
    }

    private deletePeriod(): void {
        this.employmentPeriodService
            .deleteEmploymentPeriod(this.user.id, this.selectedPeriod.id)
            .subscribe(() => {
                this.showStream.next(false);
            });
    }
}
